import React, { useCallback, useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { format, isPast, parseISO } from "date-fns";

import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Edit, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import TaskModal from "../../components/TaskModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  titleCell: {
    maxWidth: 380,
  },
  done: {
    textDecoration: "line-through",
    color: theme.palette.text.secondary,
  },
  description: {
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  overdue: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  pill: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  low: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
  },
  normal: {
    backgroundColor: theme.palette.tintedBackground,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  high: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },
}));

const FILTERS = ["mine", "open", "overdue", "done", "all"];

const isOverdue = (task) =>
  task.status === "open" && task.dueAt && isPast(parseISO(task.dueAt));

const Tasks = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [filter, setFilter] = useState("mine");
  const [search, setSearch] = useState("");
  const [tasks, setTasks] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const filterParams = useCallback(() => {
    switch (filter) {
      case "mine":
        return { status: "open", assignedUserId: user.id };
      case "open":
        return { status: "open" };
      case "overdue":
        return { overdue: "true" };
      case "done":
        return { status: "done" };
      default:
        return {};
    }
  }, [filter, user.id]);

  const fetchTasks = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const { data } = await api.get("/tasks", {
          params: {
            ...filterParams(),
            searchParam: search || undefined,
            pageNumber: page,
          },
        });
        setTasks((prev) => (page === 1 ? data.tasks : [...prev, ...data.tasks]));
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    },
    [filterParams, search]
  );

  useEffect(() => {
    setPageNumber(1);
    const timer = setTimeout(() => fetchTasks(1), 300);
    return () => clearTimeout(timer);
  }, [fetchTasks]);

  // Qualquer mudança recarrega a primeira página: mantém filtro e ordem corretos
  useEffect(() => {
    const socket = openSocket();
    socket.on("task", () => fetchTasks(1));
    return () => socket.disconnect();
  }, [fetchTasks]);

  const canEdit = (task) =>
    user.profile === "admin" ||
    task.createdById === user.id ||
    task.assignedUserId === user.id;

  const toggleDone = async (task) => {
    try {
      await api.put(`/tasks/${task.id}`, {
        status: task.status === "done" ? "open" : "done",
      });
    } catch (err) {
      toastError(err);
    }
  };

  const openModal = (taskId = null) => {
    setSelectedId(taskId);
    setModalOpen(true);
  };

  return (
    <MainContainer>
      <TaskModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedId(null);
        }}
        taskId={selectedId}
      />
      <MainHeader>
        <Title>{i18n.t("tasks.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={() => openModal()}>
            {i18n.t("tasks.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.toolbar}>
        <Tabs
          value={filter}
          onChange={(e, value) => setFilter(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          {FILTERS.map((f) => (
            <Tab key={f} value={f} label={i18n.t(`tasks.filters.${f}`)} />
          ))}
        </Tabs>
        <TextField
          size="small"
          variant="outlined"
          placeholder={i18n.t("tasks.filters.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("tasks.table.title")}</TableCell>
              <TableCell align="center">{i18n.t("tasks.table.dueAt")}</TableCell>
              <TableCell align="center">{i18n.t("tasks.table.priority")}</TableCell>
              <TableCell align="center">{i18n.t("tasks.table.assignedUser")}</TableCell>
              <TableCell align="center">{i18n.t("tasks.table.contact")}</TableCell>
              <TableCell align="center">{i18n.t("tasks.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const done = task.status === "done";
              const overdue = isOverdue(task);
              return (
                <TableRow key={task.id} hover>
                  <TableCell padding="checkbox">
                    <Tooltip title={i18n.t(done ? "tasks.reopen" : "tasks.complete")}>
                      <span>
                        <Checkbox
                          color="primary"
                          checked={done}
                          disabled={!canEdit(task)}
                          onChange={() => toggleDone(task)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.titleCell}>
                    <Typography variant="body2" className={clsx(done && classes.done)}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="caption" component="div" className={classes.description}>
                        {task.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    data-label={i18n.t("tasks.table.dueAt")}
                    className={clsx(overdue && classes.overdue)}
                  >
                    {task.dueAt
                      ? format(parseISO(task.dueAt), "dd/MM/yyyy HH:mm")
                      : i18n.t("tasks.noDue")}
                    {overdue && (
                      <Typography variant="caption" component="div">
                        {i18n.t("tasks.overdue")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("tasks.table.priority")}>
                    <span className={clsx(classes.pill, classes[task.priority])}>
                      {i18n.t(`tasks.priority.${task.priority}`)}
                    </span>
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("tasks.table.assignedUser")}>
                    {task.assignedUser?.name || "-"}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("tasks.table.contact")}>
                    {task.contact?.name || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {canEdit(task) && (
                      <IconButton size="small" onClick={() => openModal(task.id)}>
                        <Edit />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={7} />}
          </TableBody>
        </ResponsiveTable>
        {!loading && tasks.length === 0 && (
          <div className={classes.empty}>{i18n.t("tasks.empty")}</div>
        )}
        {hasMore && !loading && (
          <div className={classes.loadMore}>
            <Button
              onClick={() => {
                const next = pageNumber + 1;
                setPageNumber(next);
                fetchTasks(next);
              }}
            >
              {i18n.t("tasks.loadMore")}
            </Button>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Tasks;
